import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, InputNumber, DatePicker, Switch, App, Alert } from 'antd';
import dayjs from 'dayjs';
import cfoService from '../../Services/cfoService';
import cioService from '../../Services/cioService';
import { BillingCycleLabels, optionsFromLabels } from '../../Constants/enums';
import { toApiDate, money } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

/**
 * Set/replace a customer's commercial terms (handoff Flow D + Set Commercial
 * Terms). Loads the active pricing plans to choose from and posts numeric
 * billingCycle + a YYYY-MM-DD start date.
 */
const CommercialTermsModal = ({ open, onClose, clientId, initial, onSaved }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    cfoService
      .getPricingPlans(false)
      .then((list) => setPlans(list || []))
      .catch(() => setPlans([]));
    form.setFieldsValue({
      pricingPlanId: initial?.pricingPlanId,
      seatCount: initial?.seatCount ?? 1,
      billingCycle: initial?.billingCycle ?? 0,
      startDate: initial?.startDate ? dayjs(initial.startDate) : dayjs(),
      paymentRequiredBeforeActivation: initial?.paymentRequiredBeforeActivation ?? true,
    });
  }, [open, initial, form]);

  const onOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setSaving(true);
    try {
      await cioService.setCommercialTerms(clientId, {
        seatCount: values.seatCount,
        pricingPlanId: values.pricingPlanId,
        billingCycle: values.billingCycle,
        startDate: toApiDate(values.startDate),
        paymentRequiredBeforeActivation: values.paymentRequiredBeforeActivation,
      });
      message.success('Commercial terms saved.');
      onSaved?.();
      onClose();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const planOption = (p) => ({
    value: p.id,
    label: `${p.name} (${money(p.monthlyRatePerSeat, p.currency)}/mo, ${money(
      p.yearlyRatePerSeat,
      p.currency
    )}/yr)`,
  });

  return (
    <Modal
      title="Set Commercial Terms"
      open={open}
      onOk={onOk}
      onCancel={onClose}
      confirmLoading={saving}
      okText="Save terms"
      destroyOnHidden
    >
      {plans.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="No active pricing plans found. Create one under CFO Finance > Pricing Plans first."
        />
      )}
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item name="pricingPlanId" label="Pricing plan" rules={[{ required: true }]}>
          <Select placeholder="Select a plan" options={plans.map(planOption)} />
        </Form.Item>
        <Form.Item name="seatCount" label="Seat count" rules={[{ required: true }]}>
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="billingCycle" label="Billing cycle" rules={[{ required: true }]}>
          <Select options={optionsFromLabels(BillingCycleLabels)} />
        </Form.Item>
        <Form.Item name="startDate" label="Start date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item
          name="paymentRequiredBeforeActivation"
          label="Payment required before activation"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CommercialTermsModal;
